export class LayoutUtils {
  static readonly ADD_MENU_MARGIN_LEFT =  16
  static readonly ADD_MENU_WIDTH = 52
  static readonly ADD_MENU_AND_MARGIN_LEFT = LayoutUtils.ADD_MENU_WIDTH + LayoutUtils.ADD_MENU_MARGIN_LEFT
  static readonly INSPECTOR_WIDTH = 340
  static readonly PAGES_WIDTH = 88

  // Add-menu rail with text buttons (e.g. Batch Studio): square buttons with icon + label
  static readonly ADD_MENU_TEXT_BUTTONS_BUTTON_SIZE = 64
  static readonly ADD_MENU_TEXT_BUTTONS_CARD_PADDING = 4
  static readonly ADD_MENU_TEXT_BUTTONS_OUTER_PADDING = 16
  /** Total rail width: outer padding × 2 + card padding × 2 + button size = 104px */
  static readonly ADD_MENU_TEXT_BUTTONS_RAIL_WIDTH =
    2 * LayoutUtils.ADD_MENU_TEXT_BUTTONS_OUTER_PADDING +
    2 * LayoutUtils.ADD_MENU_TEXT_BUTTONS_CARD_PADDING +
    LayoutUtils.ADD_MENU_TEXT_BUTTONS_BUTTON_SIZE // = 104

  // Batch inspector grid gap
  static readonly BATCH_INSPECTOR_GRID_GAP = 8


  // Layout positioning constants - top
  static readonly NAVBAR_HEIGHT = 64
  static readonly NAVBAR_MARGIN = 16
  static readonly FLOATING_INSPECTOR_HEIGHT = 40

  // Layout positioning constants - bottom
  static readonly EDIT_WITH_AI_MARGIN_BOTTOM = 16
  static readonly EDIT_WITH_AI_HEIGHT = 100

  // Pixel manipulation layout constants
  static readonly IMAGE_RESOLUTION = 1280
  static readonly MASK_RESOLUTION = 1280

  // Padding constants
  static readonly CANVAS_MARGIN_LEFT = 32
  static readonly CANVAS_MARGIN_RIGHT = 32
  static readonly CANVAS_MARGIN_BOTTOM = 32
  static readonly CANVAS_MARGIN_TOP = 32
  static readonly ADD_MENU_INSPECTOR_GAP = 8

  /** Horizontal space on each side of the batch grid's cards. */
  static readonly BATCH_GRID_SIDE_SPACING = 32
  static readonly BATCH_GRID_PADDING_RIGHT = LayoutUtils.BATCH_GRID_SIDE_SPACING
  /** The add-menu rail padding, or the inspector slot's trailing gaps, already
   *  provide 16px on the left; the grid pads the rest. */
  static readonly BATCH_GRID_PADDING_LEFT =
    LayoutUtils.BATCH_GRID_SIDE_SPACING - (LayoutUtils.ADD_MENU_INSPECTOR_GAP + LayoutUtils.BATCH_INSPECTOR_GRID_GAP) // = 16

  static getFramePaddingLeft(isInspectorOpen: boolean, isAddMenuOpen: boolean): number {
    const marginLeft = isAddMenuOpen || isInspectorOpen ? LayoutUtils.ADD_MENU_MARGIN_LEFT : 0;
    const addMenuWidth = isAddMenuOpen ? LayoutUtils.ADD_MENU_WIDTH : 0;
    const gap = isInspectorOpen && isAddMenuOpen ? LayoutUtils.ADD_MENU_INSPECTOR_GAP : 0;
    const inspectorWidth = isInspectorOpen ? LayoutUtils.INSPECTOR_WIDTH : 0;
    return (marginLeft + addMenuWidth + gap + inspectorWidth);
  }

  static getFramePaddingRight(isOpenScene: boolean = true) {
    return isOpenScene ? LayoutUtils.PAGES_WIDTH : 0
  }

  static getFramePaddingTop(isOpenFloatingInspector: boolean = true, includeNavbarHeight: boolean = false) {
    return (includeNavbarHeight ? LayoutUtils.NAVBAR_HEIGHT : 0) + LayoutUtils.NAVBAR_MARGIN + (isOpenFloatingInspector ? LayoutUtils.FLOATING_INSPECTOR_HEIGHT : 0) + LayoutUtils.CANVAS_MARGIN_TOP
  }

  static getFramePaddingBottom(isEditWithAiOpen: boolean = true) {
    return isEditWithAiOpen ? 
      (LayoutUtils.EDIT_WITH_AI_MARGIN_BOTTOM + LayoutUtils.EDIT_WITH_AI_HEIGHT + LayoutUtils.CANVAS_MARGIN_BOTTOM) : 0
  }

  /**
   * Calculates dynamic padding values based on screen size
   * @param marginBottom - Additional bottom margin to consider
   * @returns Object with vertical and horizontal padding values
   */
  static calculateDynamicPadding(): { verticalPadding: number; horizontalPadding: number } {
    const verticalPadding = LayoutUtils.CANVAS_MARGIN_TOP + LayoutUtils.CANVAS_MARGIN_BOTTOM
    const horizontalPadding = LayoutUtils.CANVAS_MARGIN_LEFT + LayoutUtils.CANVAS_MARGIN_RIGHT
    return { verticalPadding, horizontalPadding }
  }

  static getMaxAvailableSize(isInspectorOpen: boolean, isAddMenuOpen: boolean, isScenesOpen: boolean): { availableWidth: number; availableHeight: number } {
    const { verticalPadding, horizontalPadding } = LayoutUtils.calculateDynamicPadding()
    const left = LayoutUtils.getFramePaddingLeft(isInspectorOpen, isAddMenuOpen)
    const right = LayoutUtils.getFramePaddingRight(isScenesOpen)
    const availableHeight = window.innerHeight - verticalPadding - LayoutUtils.NAVBAR_HEIGHT
    const availableWidth = window.innerWidth - horizontalPadding - right - left
    return { availableWidth, availableHeight }
  }

  static getAIStudioPreviewAvailableSize(
    viewport: { width: number; height: number },
    isScenesOpen: boolean = true,
  ): { availableWidth: number; availableHeight: number } {
    const { horizontalPadding } = LayoutUtils.calculateDynamicPadding()
    const left = LayoutUtils.getFramePaddingLeft(false, false)
    const right = LayoutUtils.getFramePaddingRight(isScenesOpen)

    return {
      availableWidth: viewport.width - horizontalPadding - left - right,
      availableHeight:
        viewport.height -
        LayoutUtils.NAVBAR_HEIGHT -
        LayoutUtils.getFramePaddingTop() -
        LayoutUtils.getFramePaddingBottom(),
    }
  }

  static getAIStudioPreviewFitSize(
    frame: { width: number; height: number },
    viewport: { width: number; height: number },
    isScenesOpen: boolean = true,
  ): { width: number; height: number; ratio: number } {
    const { availableWidth, availableHeight } = LayoutUtils.getAIStudioPreviewAvailableSize(viewport, isScenesOpen)
    const ratio = Math.min(availableWidth / frame.width, availableHeight / frame.height)

    return {
      width: frame.width * ratio,
      height: frame.height * ratio,
      ratio,
    }
  }

  static clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max)
  }


  /**
   * Calculates the center position for a container element
   * @param containerElement - The HTML element to center
   * @param isInspectorOpen - Whether inspector panel is open
   * @param isAddMenuOpen - Whether add menu is open
   * @param isScenesOpen - Whether scenes panel is open
   * @returns Object with top and left position values
   */
  static getCenterPosition(
    containerElement: HTMLElement,
    isInspectorOpen: boolean,
    isAddMenuOpen: boolean,
    isScenesOpen: boolean,
  ): { top: number; left: number } {
    const originalWidth = parseInt(containerElement.style.width)
    const originalHeight = parseInt(containerElement.style.height)
    const framePaddingLeft = LayoutUtils.getFramePaddingLeft(isInspectorOpen, isAddMenuOpen)
    const framePaddingRight = LayoutUtils.getFramePaddingRight(isScenesOpen)
    const containerWidth = window.innerWidth - framePaddingRight - framePaddingLeft
    const containerHeight = window.innerHeight - LayoutUtils.NAVBAR_HEIGHT

    // Center vertically
    const top = (containerHeight - originalHeight) / 2
    
    // Center horizontally - simple centering within available container width
    const left = (containerWidth - originalWidth) / 2 + framePaddingLeft

    return { top, left }
  }

  /**
   * Calculates destination ratio and dimensions for canvas layout
   * @param aspectRatio - The aspect ratio of the content
   * @param canvas - The canvas object with width/height properties
   * @param isInspectorOpen - Whether inspector panel is open
   * @param isAddMenuOpen - Whether add menu is open
   * @param marginBottom - Additional bottom margin
   * @returns Object with calculated width and height
   */
  static calculateDestinationRatio(
    canvas: { width: number; height: number },
    isInspectorOpen: boolean,
    isAddMenuOpen: boolean,
    isScenesOpen: boolean,
  ): { destinationWidth: number; destinationHeight: number } {
    const { availableWidth, availableHeight } = LayoutUtils.getMaxAvailableSize(isInspectorOpen, isAddMenuOpen, isScenesOpen)
    
    const aspectRatio = canvas.width / canvas.height
    const availableRatio = availableWidth / availableHeight
    const isWidthBased = availableRatio < aspectRatio

    let destinationWidth: number
    let destinationHeight: number

    if (isWidthBased) {
      destinationWidth = availableWidth
      destinationHeight = availableWidth / aspectRatio
    } else {
      destinationWidth = availableHeight * aspectRatio
      destinationHeight = availableHeight
    }

    return { destinationWidth, destinationHeight }
  }

  /**
   * Updates container transform matrix for recentering
   * @param containerElement - The container element to recenter
   * @param translateX - Base X translation value
   * @param isOpenInspector - Whether the inspector is open
   * @param isAddMenuOpen - Whether the add menu is open (default: true)
   */
  static recenterContainer(
    containerElement: HTMLElement,
    translateX: number,
    isOpenInspector: boolean,
    isAddMenuOpen: boolean
  ): void {
    const computedStyle = window.getComputedStyle(containerElement)
    const transformValue = computedStyle.transform
    const matrixValues = transformValue.match(/matrix\((.+)\)/)?.[1].split(', ')
    const newTranslateX = translateX + ((LayoutUtils.getFramePaddingRight() - LayoutUtils.getFramePaddingLeft(isOpenInspector, isAddMenuOpen)) / 2)
    const updatedMatrixValues = matrixValues?.map((value, index) => (index === 4 ? newTranslateX.toString() : value))
    const updatedTransformValue = `matrix(${updatedMatrixValues?.join(', ')})`

    containerElement.style.transform = updatedTransformValue
  }
}
